// Плитка элемента в пикере. Показывает те же поля, что видно в библиотеке
// (обложка, счётчик картинок, имя, описание) — раньше пикер рисовал свою
// урезанную карточку, и описание было видно только в разделе «Элементы».

import type { TFunction } from "i18next";
import { Eye, Images } from "lucide-react";

import { cn } from "~/shared/lib/utils";
import { Badge } from "~/shared/ui/badge";
import { MediaThumb } from "~/shared/ui/media-thumb";

import type { Element } from "~/entities/element";

interface ElementPickerTileProps {
    t: TFunction;
    element: Element;
    selected: boolean;
    /** Лимит выбран другими элементами — плитку нельзя выбрать, но можно посмотреть. */
    limitReached: boolean;
    onToggle: (elementId: string) => void;
    /** Просмотр элемента целиком, ДО выбора. */
    onView: (elementId: string) => void;
}

export function ElementPickerTile({
    t,
    element,
    selected,
    limitReached,
    onToggle,
    onView,
}: ElementPickerTileProps) {
    const cover = element.images[0]?.s3Url ?? null;

    return (
        <div
            className={cn(
                "relative flex flex-col overflow-hidden rounded-lg border transition-colors",
                selected ? "border-violet-400/70 bg-violet-500/10" : "border-border hover:border-white/25",
            )}
        >
            <button
                type="button"
                aria-pressed={selected}
                disabled={limitReached}
                onClick={() => onToggle(element.id)}
                className={cn("flex flex-col text-left", limitReached && "cursor-not-allowed opacity-40")}
            >
                <div className="relative aspect-square w-full bg-zinc-900">
                    {cover ? (
                        <MediaThumb url={cover} kind="image" className="absolute inset-0" />
                    ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
                            <Images className="size-7" aria-hidden />
                        </div>
                    )}
                    <Badge
                        variant="secondary"
                        className="absolute top-1.5 left-1.5 bg-black/60 font-normal text-white backdrop-blur"
                    >
                        {t("elements:card.imagesCount", { count: element.images.length })}
                    </Badge>
                </div>

                <div className="flex flex-col gap-1 px-2 py-1.5">
                    <span className="line-clamp-2 text-xs font-medium">{element.name}</span>
                    {element.description ? (
                        <span className="line-clamp-2 text-[11px] text-muted-foreground">
                            {element.description}
                        </span>
                    ) : null}
                </div>
            </button>

            {/* Просмотр отдельной кнопкой: клик по плитке выбирает элемент, и
                совмещать с ним «посмотреть» нельзя — выбор стал бы случайным. */}
            <button
                type="button"
                onClick={() => onView(element.id)}
                aria-label={t("elements:actions.view")}
                title={t("elements:actions.view")}
                className="absolute top-1.5 right-1.5 rounded-md bg-black/60 p-1.5 text-white backdrop-blur transition-colors hover:bg-black/80"
            >
                <Eye className="size-4" aria-hidden />
            </button>
        </div>
    );
}
